import axios, { AxiosInstance, AxiosError } from 'axios';
import { apiLogger } from '../utils/logger';
import type { 
  Client, 
  ClientFormData,
  ClientWithStats,
  Matter,
  MatterWithClient,
  MatterFormData,
  UpdateMatterRequest,
  HealthStatus,
  HealthDependencies,
  DatabaseHealth
} from '../types';

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

export interface RoleSettings {
  adminGroup: string;
  userGroup: string;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // Get API URL from environment variable first (highest priority)
    let baseURL = import.meta.env.VITE_API_BASE_URL;
    
    if (!baseURL && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Local development
        baseURL = 'http://localhost:7071/api';
        console.log('Detected local environment, using default:', baseURL);
      } else if (hostname.includes('azurestaticapps.net')) {
        // Azure Static Web Apps - use relative /api for linked backend
        baseURL = '/api';
        console.log('Detected Azure Static Web Apps, using /api route');
      } else {
        // Custom domain - MUST use environment variable or fallback to relative
        baseURL = '/api';
        console.log('Custom domain detected, using /api route. Ensure VITE_API_BASE_URL is set for external API.');
      }
    } else if (!baseURL) {
      // Server-side or no window object
      baseURL = 'http://localhost:7071/api';
    } else {
      console.log('Using configured API URL from VITE_API_BASE_URL:', baseURL);
    }
    
    this.client = axios.create({
      baseURL,
      timeout: 30000, // Increased timeout for long operations like data seeding
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for authentication and logging
    this.client.interceptors.request.use(
      async (config) => {
        // Add authentication header
        try {
          const { getAccessToken } = await import('../auth/authService');
          const token = await getAccessToken(['https://graph.microsoft.com/.default']);
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.warn('Failed to get access token:', error);
          // Continue without token - let the API handle authorization
        }
        
        apiLogger.api(config.method?.toUpperCase() || 'UNKNOWN', config.url || '');
        return config;
      },
      (error) => {
        apiLogger.error('API Request Error', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        apiLogger.api(
          response.config.method?.toUpperCase() || 'UNKNOWN', 
          response.config.url || '', 
          response.status
        );
        return response;
      },
      (error: AxiosError) => {
        const apiError = this.handleApiError(error);
        apiLogger.error('API Error', apiError);
        return Promise.reject(apiError);
      }
    );
  }

  private handleApiError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data as any;
      
      switch (status) {
        case 400:
          return {
            message: data?.message || 'Invalid request data',
            code: 'BAD_REQUEST',
            details: data
          };
        case 404:
          return {
            message: 'Resource not found',
            code: 'NOT_FOUND'
          };
        case 500:
          return {
            message: 'Internal server error. Please try again later.',
            code: 'SERVER_ERROR'
          };
        default:
          return {
            message: data?.message || `Server error (${status})`,
            code: 'SERVER_ERROR'
          };
      }
    } else if (error.request) {
      // Request was made but no response received (could be cold start)
      return {
        message: 'Unable to connect to server. Please check your connection.',
        code: 'NETWORK_ERROR',
        details: { 
          possibleColdStart: true,
          retryRecommended: true 
        }
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR'
      };
    }
  }

  // Helper method to retry requests on potential cold starts
  private async makeRequestWithRetry<T>(requestFn: () => Promise<T>, retries = 1): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (error.code === 'NETWORK_ERROR' && retries > 0) {
        console.log('Potential cold start detected, retrying in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.makeRequestWithRetry(requestFn, retries - 1);
      }
      throw error;
    }
  }

  // Warmup method to wake up the API
  async warmupApi(): Promise<boolean> {
    try {
      console.log('Warming up API...');
      await this.client.get('/health/status');
      console.log('API warmed up successfully');
      return true;
    } catch (error) {
      console.warn('API warmup failed:', error);
      return false;
    }
  }

  // Client API methods with retry logic for critical operations
  async getClients(): Promise<Client[]> {
    return this.makeRequestWithRetry(async () => {
      const response = await this.client.get<ApiResponse<Client[]>>('/clients');
      return response.data.data || response.data;
    });
  }

  async getMatters(): Promise<Matter[]> {
    return this.makeRequestWithRetry(async () => {
      const response = await this.client.get<ApiResponse<Matter[]>>(`/matters?includeClient=false`);
      return response.data.data || response.data;
    });
  }

  // New method to get matters with client information for enhanced displays
  async getMattersWithClients(): Promise<MatterWithClient[]> {
    return this.makeRequestWithRetry(async () => {
      const response = await this.client.get<ApiResponse<MatterWithClient[]>>(`/matters?includeClient=true`);
      return response.data.data || response.data;
    });
  }

  async getClient(id: string): Promise<Client> {
    const response = await this.client.get<ApiResponse<Client>>(`/clients/${id}`);
    return response.data.data || response.data;
  }

  async createClient(clientData: ClientFormData): Promise<Client> {
    const response = await this.client.post<ApiResponse<Client>>('/clients', clientData);
    return response.data.data || response.data;
  }

  async updateClient(id: string, clientData: ClientFormData): Promise<Client> {
    const response = await this.client.put<ApiResponse<Client>>(`/clients/${id}`, clientData);
    return response.data.data || response.data;
  }

  async deleteClient(id: string): Promise<void> {
    await this.client.delete(`/clients/${id}`);
  }

  // Client details with statistics
  async getClientDetails(id: string): Promise<ClientWithStats> {
    const response = await this.client.get<ApiResponse<ClientWithStats>>(`/clients/${id}/details`);
    return response.data.data || response.data;
  }

  // Matter API methods
  async getClientMatters(clientId: string): Promise<Matter[]> {
    const response = await this.client.get<ApiResponse<Matter[]>>(`/clients/${clientId}/matters?includeClient=false`);
    return response.data.data || response.data;
  }

  // New method to get client matters with client information
  async getClientMattersWithClient(clientId: string): Promise<MatterWithClient[]> {
    const response = await this.client.get<ApiResponse<MatterWithClient[]>>(`/clients/${clientId}/matters?includeClient=true`);
    return response.data.data || response.data;
  }

  async getMatter(matterId: string): Promise<Matter> {
    const response = await this.client.get<ApiResponse<Matter>>(`/matters/${matterId}`);
    return response.data.data || response.data;
  }

  async getMatterWithClient(matterId: string): Promise<MatterWithClient> {
    const response = await this.client.get<ApiResponse<MatterWithClient>>(`/matters/${matterId}?includeClient=true`);
    return response.data.data || response.data;
  }

  async createMatter(clientId: string, matterData: MatterFormData): Promise<Matter> {
    const response = await this.client.post<ApiResponse<Matter>>(`/matters`, { 
      ...matterData, 
      clientId 
    });
    return response.data.data || response.data;
  }

  async updateMatter(_clientId: string, matterId: string, matterData: UpdateMatterRequest): Promise<Matter> {
    // Note: _clientId parameter kept for consistency and validation, though backend uses matterId directly
    const response = await this.client.put<ApiResponse<Matter>>(`/matters/${matterId}`, matterData);
    return response.data.data || response.data;
  }

  async deleteMatter(_clientId: string, matterId: string): Promise<void> {
    // Note: _clientId parameter kept for consistency and validation, though backend uses matterId directly
    await this.client.delete(`/matters/${matterId}`);
  }

  // Medical Records - Uses existing document system with 'medical_records' category

  // Health check methods
  async getHealthStatus(): Promise<HealthStatus> {
    const response = await this.client.get('/health/status');
    return response.data;
  }

  async getHealthDependencies(): Promise<HealthDependencies> {
    const response = await this.client.get('/health/dependencies');
    return response.data;
  }

  async getDatabaseHealth(): Promise<DatabaseHealth> {
    const response = await this.client.get('/health/database');
    return response.data;
  }

  // SharePoint Embedded Container Management (Backend)
  async ensureMatterContainer(matterId: string, matterName: string, clientId: string): Promise<{
    success: boolean;
    data?: {
      containerId: string;
      containerUrl: string;
      isNew: boolean;
      matterId: string;
      clientId: string;
    };
    message?: string;
    error?: string;
  }> {
    // Retry mechanism for container operations since they can be affected by cold starts
    return this.makeRequestWithRetry(async () => {
      const response = await this.client.post(`/matters/${matterId}/ensure-container`, {
        matterName,
        clientId
      });
      return response.data;
    }, 2); // Retry once on failure
  }

  // Role Settings methods
  async getRoleSettings(): Promise<RoleSettings> {
    const response = await this.client.get<ApiResponse<RoleSettings>>('/role-settings');
    return response.data.data || response.data;
  }

  async updateRoleSettings(settings: RoleSettings): Promise<RoleSettings> {
    const response = await this.client.put<ApiResponse<RoleSettings>>('/role-settings', settings);
    return response.data.data || response.data;
  }

  // Admin / Development methods
  async seedSampleData(): Promise<{ success: boolean; clientsCreated: number; mattersCreated: number; message?: string }> {
    const response = await this.client.post('/seedData?confirm=true');
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
